import React, { useState } from 'react';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';

const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

const VillaBookingModal = ({ isOpen, onClose, villa }) => {
    useLockBodyScroll(isOpen);

    const [formData, setFormData] = useState({
        guestName: '', email: '', phone: '',
        checkIn: '', checkOut: '', guests: 1, message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/villa-bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    guests: parseInt(formData.guests),
                    villaId: villa?.id || villa?._id || null,
                    villaName: villa?.title || 'Unknown Property',
                })
            });
            if (!res.ok) throw new Error('Failed to submit booking');
            setSuccess(true);
            setTimeout(() => { setSuccess(false); onClose(); setFormData({ guestName: '', email: '', phone: '', checkIn: '', checkOut: '', guests: 1, message: '' }); }, 2500);
        } catch (err) {
            alert('Error submitting enquiry: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-hidden" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto overscroll-contain">
                <div className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white p-6 rounded-t-2xl flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-bold">Book / Enquire</h3>
                        <p className="text-teal-100 text-sm mt-1">{villa?.title}</p>
                    </div>
                    <button onClick={onClose} className="text-white text-2xl hover:text-gray-200 transform hover:rotate-90 transition"><i className="fas fa-times"></i></button>
                </div>

                {success ? (
                    <div className="p-12 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-check text-4xl text-green-500"></i>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Enquiry Sent!</h3>
                        <p className="text-gray-600">We'll get back to you shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Full Name *</label>
                                <input name="guestName" value={formData.guestName} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Email *</label>
                                <input name="email" type="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Phone *</label>
                                <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Number of Guests *</label>
                                <input name="guests" type="number" min="1" max={villa?.maxGuests || 20} value={formData.guests} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Check-In Date *</label>
                                <input name="checkIn" type="date" value={formData.checkIn} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Check-Out Date *</label>
                                <input name="checkOut" type="date" value={formData.checkOut} onChange={handleChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-1">Message</label>
                            <textarea name="message" value={formData.message} onChange={handleChange} rows={3} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-teal focus:outline-none" placeholder="Any special requirements or questions..." />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition">Cancel</button>
                            <button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition disabled:opacity-60">
                                {submitting ? <><i className="fas fa-spinner fa-spin mr-2"></i>Sending...</> : <><i className="fas fa-paper-plane mr-2"></i>Send Enquiry</>}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default VillaBookingModal;
